import { useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Alert, Avatar, Box, Button, CircularProgress, Container, Grid2 as Grid, IconButton, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import InputMask from "react-input-mask";
import { File02Icon, LinkSquare02Icon, MailSend01Icon, MarketingIcon, Shield01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useUpdateUserMutation } from "@/api/usersApi";
import PageHeader from "@/components/PageHeader";
import { passwordReset } from "@/features/auth/slice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import { stringToColor, getInitials } from "@/utils/names";
import { sanitizePhone } from "@/utils/phones";
import LegalCard from "../legal/LegalCard";
import { LegalPreviewDrawer } from "../legal/LegalConsentDrawer";
import { getAllLegalDocuments } from "@/constants/legal";
import { useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { OrganizationContext } from "@/features/organizations/components/OrganizationLayout";
import { useOutletContext } from "react-router-dom";
import { LegalDocument } from "@/types/legal";

const validationSchema = Yup.object({
  name: Yup.string().max(50, "Muy Largo!").required("Campo requerido"),
  lastname: Yup.string().max(50, "Muy Largo!").required("Campo requerido"),
  phone: Yup.string()
    .min(8, "Muy Corto!")
    .max(8, "Muy Largo!")
    .matches(/^[0-9+\s-]*$/, "Sólo números, espacios y +")
    .required("Campo requerido"),
});

const UserProfile = () => {
  const user = useAppSelector((store) => store.auth.user);
  const container = useAppSelector((state) => state.ui?.container);
  const [updateUser] = useUpdateUserMutation();
  const [openSnackbar] = useSnackbar();
  const dispatch = useAppDispatch();
  const [emailSent, setEmailSent] = useState(false);
  const theme = useTheme();

  // Estado para el drawer de preview
  const [previewDoc, setPreviewDoc] = useState<LegalDocument | null>(null);

  const { currentOrganization } = useOutletContext<OrganizationContext>();
  const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();
  const legalDocs = getAllLegalDocuments();

  // Inicializar el estado de aceptación basado en lo que ya tiene la organización
  const [accepted, setAccepted] = useState({
    terms: currentOrganization?.legal?.terms?.version === legalDocs.terms.version,
    privacy: currentOrganization?.legal?.privacy?.version === legalDocs.privacy.version,
    marketing: currentOrganization?.legal?.marketing?.accepted || false,
  });

  const needsAcceptance = useMemo(() => {
    if (!currentOrganization?.legal) return true;
    if (currentOrganization.legal.terms?.version !== legalDocs.terms.version) return true;
    if (currentOrganization.legal.privacy?.version !== legalDocs.privacy.version) return true;
    return false;
  }, [currentOrganization, legalDocs]);


  const toggleLegalCheck = (key: "terms" | "privacy" | "marketing", checked: boolean) => {
    setAccepted((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSaveAcceptance = async () => {
    if (!currentOrganization?.id || !user?.uid) {
      openSnackbar("Error: No se pudo identificar al usuario o la organización", "error");
      return;
    }

    if (!accepted.terms || !accepted.privacy) {
      openSnackbar("Debes aceptar los Términos y la Política de Privacidad", "error");
      return;
    }

    const legalPayload = {
      terms: {
        version: legalDocs.terms.version,
        acceptedAt: Date.now(),
        acceptedBy: user.uid,
      },
      privacy: {
        version: legalDocs.privacy.version,
        acceptedAt: Date.now(),
        acceptedBy: user.uid,
      },
      marketing: {
        accepted: accepted.marketing,
        acceptedAt: accepted.marketing ? Date.now() : undefined,
        acceptedBy: accepted.marketing ? user.uid : undefined,
      }
    };

    try {
      await updateOrganization({
        organizationId: currentOrganization.id,
        payload: { legal: legalPayload }
      }).unwrap();

      openSnackbar("Acuerdos legales actualizados correctamente", "success");
    } catch (error) {
      console.error(error);
      openSnackbar("Error al guardar los acuerdos legales", "error");
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    await dispatch(passwordReset(user?.email));
    setEmailSent(true);
  };

  return (
    <Stack spacing={3} flex={1} height={'100vh'}>
      <PageHeader title="Perfil de Usuario" />
      <Stack>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >
          <Grid container spacing={2} alignContent="flex-start" height="100%">
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stack spacing={2}>
                <Paper
                  sx={{
                    p: 2,
                  }}
                >
                  <Stack justifyContent={"center"} alignItems={"center"} spacing={2} paddingTop={2}>
                    {user?.image ? (
                      <Avatar src={user.image} alt="Foto de perfil" sx={{ width: 100, height: 100 }} />
                    ) : (
                      <Avatar
                        sx={{
                          width: 100,
                          height: 100,
                          backgroundColor: stringToColor(`${user?.name} ${user?.lastname}`),
                        }}
                      >
                        {getInitials(`${user?.name ?? ""} ${user?.lastname ?? ""}`)}
                      </Avatar>
                    )}
                    {user?.name ? (
                      <Typography variant="h4" sx={{ textAlign: "center" }}>
                        {user.name} {user?.lastname ?? ""}
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Stack spacing={2}>
                <Formik
                  initialValues={{
                    name: user.name ?? "",
                    lastname: user.lastname ?? "",
                    phone: user.phone ?? "",
                    email: user.email ?? "",
                  }}
                  enableReinitialize
                  validationSchema={validationSchema}
                  onSubmit={async (values) => {
                    try {
                      await updateUser({
                        draft: {
                          uid: user.uid,
                          ...values,
                        },
                      });
                      openSnackbar("Datos Actualizados", "success");
                    } catch (error) {
                      openSnackbar("Hubo un error al actualizar tu cuenta", "success");
                    }
                  }}
                >
                  {({ values, setFieldValue, errors, touched, handleChange, handleSubmit, handleBlur }) => {
                    return (
                      <form onSubmit={handleSubmit} style={{ height: "100%" }}>
                        <Stack spacing={3} height={"100%"}>
                          <Paper
                            sx={{
                              p: 2,
                            }}
                          >
                            <Stack spacing={2}>
                              <Typography variant="h4">Información Personal</Typography>
                              <Stack spacing={2} direction={"row"}>
                                <TextField
                                  label="Nombre"
                                  name="name"
                                  value={values.name}
                                  onChange={handleChange}
                                  fullWidth
                                  size="small"
                                  error={touched.name && Boolean(errors.name)}
                                  helperText={touched.name && errors.name}
                                  onBlur={handleBlur}
                                />

                                <TextField
                                  label="Apellido"
                                  name="lastname"
                                  value={values.lastname}
                                  onChange={handleChange}
                                  fullWidth
                                  size="small"
                                  error={touched.lastname && Boolean(errors.lastname)}
                                  helperText={touched.lastname && errors.lastname}
                                  onBlur={handleBlur}
                                />
                              </Stack>

                              <Stack spacing={2}>
                                <InputMask
                                  mask="9999-9999"
                                  maskChar={null}
                                  placeholder="0000-0000"
                                  alwaysShowMask
                                  value={values.phone}
                                  onChange={(e) => {
                                    setFieldValue("phone", sanitizePhone(e.target.value));
                                  }}
                                  onBlur={handleBlur}
                                >
                                  {() => (
                                    <TextField label="Teléfono" name="phone" size="small" error={touched.phone && Boolean(errors.phone)} helperText={touched.phone && errors.phone} />
                                  )}
                                </InputMask>

                                <TextField label="Correo" name="email" value={values.email} fullWidth size="small" disabled />
                              </Stack>

                              <span style={{ display: "flex", justifyContent: "center" }}>
                                <Button type="submit" variant="contained">
                                  Actualizar
                                </Button>
                              </span>
                            </Stack>
                          </Paper>

                          <Paper
                            sx={{
                              p: 2,
                            }}
                          >
                            <Stack spacing={2}>
                              {emailSent ? (
                                <Alert color="success" severity="success">
                                  Correo enviado, Revisa tu bandeja de entrada!
                                </Alert>
                              ) : null}
                              <Typography variant="h3">Cambiar Contraseña</Typography>
                              <Typography variant="body1">Haz click en el botón de abajo para recibir un correo de cambio de contraseña</Typography>
                              <span style={{ display: "flex", justifyContent: "center" }}>
                                <Button variant="outlined" color="primary" onClick={handleResetPassword} endIcon={<HugeiconsIcon icon={MailSend01Icon} />}>
                                  Enviar enlace
                                </Button>
                              </span>
                            </Stack>
                          </Paper>
                        </Stack>
                      </form>
                    );
                  }}
                </Formik>
                {/* Sección de Acuerdos Legales */}
                <Paper sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        Acuerdos Legales de la Organización {currentOrganization?.title}
                      </Typography>
                      {needsAcceptance && (
                        <Typography variant="h5" sx={{ color: "warning.main", fontWeight: 600, bgcolor: "warning.lighter", px: 1, py: 0.5, borderRadius: 1 }}>
                          Requiere actualización
                        </Typography>
                      )}
                    </Stack>

                    <Stack spacing={2}>
                      <LegalCard
                        checked={accepted.terms}
                        onChange={(checked) => toggleLegalCheck("terms", checked)}
                        title="Términos y Condiciones"
                        description="Conozco las reglas y lineamientos legales para el uso de la plataforma."
                        icon={<HugeiconsIcon icon={File02Icon} color={theme.palette.primary.main} />}
                        link={
                          <IconButton onClick={() => setPreviewDoc(legalDocs.terms)} size="small">
                            <HugeiconsIcon icon={LinkSquare02Icon} color={theme.palette.primary.main} />
                          </IconButton>
                        }
                        tag={`v${legalDocs.terms.version}`}
                        disabled={!needsAcceptance}
                        hideCheckbox={!needsAcceptance}
                      />

                      <LegalCard
                        checked={accepted.privacy}
                        onChange={(checked) => toggleLegalCheck("privacy", checked)}
                        title="Política de Privacidad"
                        description="Entiendo cómo son manejados mis datos personales y privacidad."
                        icon={<HugeiconsIcon icon={Shield01Icon} color={theme.palette.primary.main} />}
                        link={
                          <IconButton onClick={() => setPreviewDoc(legalDocs.privacy)} size="small">
                            <HugeiconsIcon icon={LinkSquare02Icon} color={theme.palette.primary.main} />
                          </IconButton>
                        }
                        tag={`v${legalDocs.privacy.version}`}
                        disabled={!needsAcceptance}
                        hideCheckbox={!needsAcceptance}
                      />

                      <LegalCard
                        checked={accepted.marketing}
                        onChange={(checked) => toggleLegalCheck("marketing", checked)}
                        title="Novedades y Marketing"
                        description="¡Me gustaría recibir novedades y consejos útiles!"
                        icon={<HugeiconsIcon icon={MarketingIcon} color={theme.palette.primary.main} />}
                        disabled={!needsAcceptance}
                        hideCheckbox={!needsAcceptance}
                      />
                    </Stack>

                    {needsAcceptance && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button
                          variant="contained"
                          disabled={!accepted.terms || !accepted.privacy || isUpdating}
                          onClick={handleSaveAcceptance}
                          sx={{ minWidth: 200 }}
                        >
                          {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Guardar Acuerdos"}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Stack>
      {/* Drawer de Preview (Reutilizado) */}
      <LegalPreviewDrawer document={previewDoc} onClose={() => setPreviewDoc(null)} />
    </Stack>
  );
};

export default UserProfile;
